import { StatusType } from 'libs/gutClient/types';
import type { TreeNode, Node } from 'libs/gutClient/types';
import type { ExtendedTreeNode, SelectionState } from './types';

// export const buildNodeId = node => `${node.path}:${node.hash}`;

const getFlattenedNodeIds = (node: ExtendedTreeNode): string[] => {
  if (!node.tree) return [node.fullPath];
  return [node.fullPath, ...node.tree.flatMap(getFlattenedNodeIds)];
};

export const resolveIdSelection = (
  addedIds: string[],
  removedIds: string[],
  oldIds: string[],
  node: ExtendedTreeNode,
) => {
  const resultSet = new Set(oldIds);
  addedIds.forEach(addedId => {
    const addedTreeNode = node.tree.find(item => item.fullPath === addedId);
    const innerFlattenedItems = getFlattenedNodeIds(addedTreeNode);
    innerFlattenedItems.forEach(item => {
      resultSet.add(item);
    });
  });
  removedIds.forEach(removedId => {
    const removedTreeNode = node.tree.find(item => item.fullPath === removedId);
    if (!removedTreeNode) return;
    const innerFlattenedItems = getFlattenedNodeIds(removedTreeNode);
    innerFlattenedItems.forEach(item => {
      resultSet.delete(item);
    });
  });
  return Array.from(resultSet);
};

export const getPathContentsFromTree = (
  tree: ExtendedTreeNode[],
  path: string,
) => {
  const splitPath = path.split('/').filter(segment => segment !== '');
  let treeCursor = tree;
  splitPath.forEach(segment => {
    const nextNode = treeCursor?.find(treeNode => treeNode.path === segment);
    if (!nextNode) {
      console.error(
        `getPathContentsFromTree: Path ${path} does not exist. There is no ${segment} directory.`,
      );
    } else if (nextNode?.tree) {
      treeCursor = nextNode.tree;
    } else {
      treeCursor = [nextNode];
    }
  });
  return treeCursor || []; // || [] is test code so that I can go in not yet commited folders through old code
};

export const getTreeNodeFromPath = (node: ExtendedTreeNode, path: string) => {
  const splitPath = path.split('/').filter(segment => segment !== '');
  let treeCursor = node;
  splitPath.forEach(segment => {
    treeCursor = treeCursor?.tree.find(treeNode => treeNode.path === segment);
    if (!treeCursor) {
      console.error(
        `getTreeNodeFromPath: Path ${path} does not exist. There is no ${segment} directory.`,
      );
    }
  });
  return treeCursor;
};

export const getLastSegment = (path: string) =>
  path.substring(path.lastIndexOf('/') + 1);

export const containingFolderPath = (path: string) =>
  path.substring(0, path.lastIndexOf('/'));

export const pathCompare = (pathA: string, pathB: string) => {
  const containingFolderPathA = containingFolderPath(pathA);
  const containingFolderPathB = containingFolderPath(pathB);
  const prefixComp = containingFolderPathA.localeCompare(containingFolderPathB);
  if (prefixComp === 0) {
    return pathA.localeCompare(pathB);
  }
  return prefixComp;
};

export const concatPaths = (a: string, b: string) => {
  // eslint-disable-next-line no-param-reassign
  if (b === '.' || b === '/') b = '';
  let optSlash = '';
  if (!a.endsWith('/') && a !== '') optSlash = '/';
  if (b.startsWith('/')) optSlash = '';
  let concatenated = `${a}${optSlash}${b}`;
  if (concatenated.endsWith('/') && concatenated.length > 1)
    concatenated = concatenated.substring(0, concatenated.length - 1);
  return concatenated;
};

export const mapChangeTypeGQLOpType = (gutOpType: string) => {
  if (gutOpType === StatusType.Added) return 'ADD';
  if (gutOpType === StatusType.Modified) return 'UPDATE';
  // if (gutOpType === 'moved') return 'RENAME';
  if (gutOpType === StatusType.Deleted) return 'REMOVE';
  throw new Error(`Unkown gut operation type: ${gutOpType}`);
};

export const extendTreeNode = (
  rootNode: TreeNode,
  selectedIds: string[],
  initialPath = '',
) => {
  const selectedIdsSet = new Set(selectedIds);
  const traverse = (node: TreeNode | Node, currentPath: string) => {
    const extendedNode: ExtendedTreeNode = {
      ...node,
      tree: undefined,
      selection: null,
      fullPath: null,
    };
    let nodeSelectionStatus: SelectionState = 'unselected';
    const fullPath = concatPaths(currentPath, node.path);
    if ((node as TreeNode).tree) {
      extendedNode.tree = [];

      const treeNode: TreeNode = node as TreeNode;
      let numSelected = 0;
      let numPartiallySelected = 0;
      treeNode.tree.forEach(childNode => {
        const extendedChildNode = traverse(childNode, fullPath);
        extendedNode.tree.push(extendedChildNode);
        if (extendedChildNode.selection === 'selected') numSelected += 1;
        if (extendedChildNode.selection === 'partiallySelected')
          numPartiallySelected += 1;
      });
      if (numSelected === treeNode.tree.length) {
        nodeSelectionStatus = 'selected';
      } else if (numSelected + numPartiallySelected > 0) {
        nodeSelectionStatus = 'partiallySelected';
      }
    } else if (selectedIdsSet.has(fullPath)) {
      nodeSelectionStatus = 'selected';
    }

    extendedNode.selection = nodeSelectionStatus;
    extendedNode.fullPath = fullPath;
    return extendedNode;
  };

  return traverse(rootNode, initialPath);
};

// Just calculates (a/b)% but doesn't trust a thing
export const carefulPercent = (a: number, b: number) => {
  if (
    typeof a !== 'number' ||
    // eslint-disable-next-line no-restricted-globals
    isNaN(a) ||
    typeof b !== 'number' ||
    // eslint-disable-next-line no-restricted-globals
    isNaN(b) ||
    b === 0
  ) {
    return 0;
  }

  const percentage = (a / b) * 100;

  return Math.min(100, Math.max(0, percentage));
};

// from: https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
export const humanFileSize = (bytes: number, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    // eslint-disable-next-line no-param-reassign
    bytes /= thresh;
    u += 1;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return `${bytes.toFixed(dp)} ${units[u]}`;
};

export const splitArrayInBatches = (arr: any[], size: number) => {
  const res = [];
  let i = 0;
  while (i < arr.length) {
    res.push(arr.slice(i, i + size));
    i += size;
  }
  return res;
};

const defaultOptions = {
  concurrencySize: 1,
  mergeResults: ((a: any, b: any) => [...a, ...b]) as (a: any, b: any) => any,
};

export const batchPromiseAll = (
  arr: any[],
  batchSize: number,
  options = defaultOptions,
) => {
  let { concurrencySize, mergeResults } = options;
  if (!concurrencySize) ({ concurrencySize } = defaultOptions);
  if (!mergeResults) ({ mergeResults } = defaultOptions);

  return async (f: (a: any[], ...rest: any[]) => any, ...extraArrgs: any[]) => {
    let index = 0;
    let mergedResult: any = [];
    while (index < arr.length) {
      const concurrentBatches = [];
      while (index < arr.length && concurrentBatches.length < concurrencySize) {
        concurrentBatches.push(arr.slice(index, index + batchSize));
        index += batchSize;
      }
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.all(
        concurrentBatches.map(batch => f(batch, ...extraArrgs)),
      );
      const allResults = results.flat(1);
      mergedResult = mergeResults(mergedResult, allResults);
    }
    return mergedResult;
  };
};

const defaultOptions2 = {
  concurrencySize: 1,
  mergeResults: (a: boolean, b: boolean) => a && b,
};

export const batchSuccessFunction = (
  arr: any[],
  batchSize: number,
  options = defaultOptions2,
) => {
  let { concurrencySize, mergeResults } = options;
  if (!concurrencySize) ({ concurrencySize } = defaultOptions);
  if (!mergeResults) ({ mergeResults } = defaultOptions);

  return async (f: (a: any[]) => Promise<boolean>) => {
    let index = 0;
    let allSuccessful: boolean = true;
    while (index < arr.length && allSuccessful) {
      const concurrentBatches = [];
      while (index < arr.length && concurrentBatches.length < concurrencySize) {
        concurrentBatches.push(arr.slice(index, index + batchSize));
        index += batchSize;
      }
      // eslint-disable-next-line no-await-in-loop
      const successes = await Promise.all(concurrentBatches.map(f));
      const allBatchesSuccessful = successes.reduce(
        (prev, curr) => prev && curr,
        true,
      );
      allSuccessful = mergeResults(allSuccessful, allBatchesSuccessful);
    }
    return allSuccessful;
  };
};

const illegalCharacters = ['*', '"', '<', '>', ':', '|', '?', '\0'];

export const isIllegalPath = (path: string) =>
  illegalCharacters.some(c => path.includes(c));
